import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { Box, Paper, CircularProgress, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { PERMISSION_CODES } from "../../../utils/constants/permissions";
import { getAgencyIdFromUser } from "../../../utils/agencyScope";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useUrlPaging from "../../../hooks/useUrlPaging";
import usePermissions from "../../../hooks/usePermissions";
import useZustandStore from "../../../zustandStore/useZustandStore";
import AdsAPIs from "../../../apiRoutes/Ads";
import PaginationFooter from "../../common/PaginationFooter";
import AdDeliveryExportDropDown from "../AdDeliveryExportDropDown";
import ResubmitAssetModal from "../Modals/ResubmitAssetModal";
import DeliveryFilterBar from "./DeliveryFilterBar";
import DeliveryStatCards from "./DeliveryStatCards";
import DeliveriesTable from "./DeliveriesTable";

const AdDeliverList = ({
  onOpenDelivery,
  onDeliverRequest,
  rejectedOnly = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [filters, setFilters] = useState(null);
  const { page, setPage, pageSize, setPageSize } = useUrlPaging();
  const [resubmitDelivery, setResubmitDelivery] = useState(null);
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canExportDeliveries = hasPermission(PERMISSION_CODES.AD_DELIVER_VIEW);
  const canCreateDelivery = hasPermission(PERMISSION_CODES.AD_DELIVER_CREATE);
  const currentUser = useZustandStore((s) => s?.currentUser);
  const agencyId = getAgencyIdFromUser(currentUser);

  const retryS3Mutation = useMutation({
    mutationFn: (id) => AdsAPIs.retryS3Migration({ axiosPrivate, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adDeliveries"] });
      toast.success("S3 migration queued");
    },
  });

  const handleFiltersChange = useCallback(
    (next) => setFilters((prev) => (prev?.key === next.key ? prev : next)),
    []
  );

  const resetKey = `${filters?.key}|${status}|${rejectedOnly}|${agencyId}`;
  const lastResetKey = useRef(null);
  useEffect(() => {
    if (!filters) return;
    if (lastResetKey.current && lastResetKey.current !== resetKey) setPage(0);
    lastResetKey.current = resetKey;
  }, [filters, resetKey, setPage]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (status) next.set("status", status);
        else next.delete("status");
        return next;
      },
      { replace: true }
    );
  }, [status, setSearchParams]);

  const buildListParams = (limit, offset, overrides = {}) => ({
    axiosPrivate,
    ...filters?.apiParams,
    delivered_by_agency_id: agencyId || undefined,
    finalized_rejected_only: rejectedOnly || undefined,
    moderation_status_filter: status || undefined,
    include_pending: !rejectedOnly || undefined,
    limit,
    offset,
    ...overrides,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: [
      "adDeliveries",
      agencyId,
      filters?.key,
      status,
      rejectedOnly,
      page,
      pageSize,
    ],
    queryFn: () =>
      AdsAPIs.listDeliveries(buildListParams(pageSize, page * pageSize)),
    placeholderData: keepPreviousData,
    enabled: !!filters,
  });

  const { search, date_from, date_to } = filters?.apiParams ?? {};
  const { data: rejectedCountResult } = useQuery({
    queryKey: [
      "adDeliveries",
      "rejectedCount",
      agencyId,
      search,
      date_from,
      date_to,
    ],
    queryFn: () =>
      AdsAPIs.listDeliveries({
        axiosPrivate,
        delivered_by_agency_id: agencyId || undefined,
        search,
        date_from,
        date_to,
        finalized_rejected_only: true,
        limit: 1,
        offset: 0,
      }),
    enabled: !rejectedOnly && !!filters,
  });

  const deliveries = result?.data ?? [];

  return (
    <>
      {!rejectedOnly && (
        <DeliveryStatCards
          deliveredTotal={result?.deliveredCount ?? 0}
          pendingTotal={result?.pendingCount ?? 0}
          rejectedTotal={rejectedCountResult?.count ?? 0}
          onToggleAwaiting={() =>
            setStatus(status === "delivery_pending" ? "" : "delivery_pending")
          }
        />
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <div className="p-4 space-y-3 border-b border-gray-200 bg-white">
          <DeliveryFilterBar
            rejectedOnly={rejectedOnly}
            status={status}
            onStatusChange={setStatus}
            agencyId={agencyId}
            onChange={handleFiltersChange}
            actions={
              canExportDeliveries && (
                <AdDeliveryExportDropDown
                  buildListParams={buildListParams}
                  disabled={isLoading || !filters}
                />
              )
            }
          />
        </div>

        {!rejectedOnly && (
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              display: "block",
              bgcolor: "#fafafa",
              color: "text.secondary",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            A <strong>Delivery pending</strong> row is a request with nothing
            delivered against it yet — it has no delivery ID, and no moderation
            or QC stage to report. A filled <strong>Final approved</strong> chip
            is a locked approval, ready for launch. An outlined{" "}
            <strong>Rejected</strong> chip is the moderator's decision and can
            still change. <strong>Resubmit</strong> appears after a rejection,
            when no newer delivery exists for the same hook.
          </Typography>
        )}
        <Box
          sx={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {!result ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              minHeight={320}
              gap={2}
            >
              <CircularProgress size={28} sx={{ color: "#7f7ad5" }} />
              <Typography
                component="span"
                fontWeight={500}
                sx={{ color: "#7f7ad5" }}
              >
                {rejectedOnly
                  ? "Loading rejected deliveries…"
                  : "Loading deliveries…"}
              </Typography>
            </Box>
          ) : (
            <DeliveriesTable
              deliveries={deliveries}
              rejectedOnly={rejectedOnly}
              canCreateDelivery={canCreateDelivery}
              onOpenDelivery={onOpenDelivery}
              onOpenRequest={(id) => navigate(`/dashboard/ad-request/${id}`)}
              onDeliverRequest={onDeliverRequest}
              onResubmit={setResubmitDelivery}
              retryS3={retryS3Mutation}
            />
          )}
          <PaginationFooter
            shown={deliveries.length}
            totalCount={result?.count ?? 0}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(0);
            }}
            extra={
              rejectedOnly ? (
                <button
                  type="button"
                  className="text-sm font-medium text-[#7f7ad5] hover:underline mr-2"
                  onClick={() =>
                    setSearchParams((p) => {
                      const next = new URLSearchParams(p);
                      next.delete("rejected");
                      return next;
                    })
                  }
                >
                  All deliveries
                </button>
              ) : null
            }
          />
        </Box>
      </Paper>

      <ResubmitAssetModal
        open={!!resubmitDelivery}
        onClose={() => setResubmitDelivery(null)}
        delivery={resubmitDelivery}
        agencyId={agencyId}
      />
    </>
  );
};

export default AdDeliverList;
